/* prettier-ignore */
/**
 * Extracción de URLs del texto del asistente para generar link-previews (HRS-2498, TNI).
 *
 * Objetivo: cazar también dominios "pelados" (sin http/www, p.ej. `discovernorthernireland.com`)
 * MINIMIZANDO falsos positivos (el motivo por el que se quitó en su momento). Heurísticas:
 *  - URLs explícitas (http(s):// o www.) → validación laxa (el bot las escribió enteras).
 *  - Dominios pelados → validación ESTRICTA: host en minúsculas, whitelist de TLDs,
 *    TLD como token completo (lookahead de frontera), label registrable con ≥1 letra,
 *    y frontera por delante (no formar parte de un token mayor). Emails fuera primero.
 *
 * Función PURA (solo usa `URL`), testable en aislado — ver url-extract.util.spec.ts.
 */

// TLDs aceptados para dominios PELADOS (no afecta a URLs explícitas con http/www).
export const ALLOWED_TLDS = [
  'com', 'org', 'net', 'info', 'biz', 'io', 'co', 'app', 'dev', 'travel', 'tourism',
  'gov', 'edu', 'eu', 'es', 'ie', 'uk', 'de', 'fr', 'it', 'pt', 'nl', 'be', 'ch',
  'at', 'se', 'no', 'fi', 'dk', 'pl', 'ru', 'us', 'cat', 'gal',
];

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const EXPLICIT_RE = /\b(?:https?:\/\/|www\.)[^\s<>"')\]]+/gi;
// Dominio pelado: 1+ labels en MINÚSCULAS + TLD whitelisteado como token completo + path opcional.
const BARE_RE = new RegExp(
  '(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:' + ALLOWED_TLDS.join('|') + ')' +
  '(?![a-z0-9-])(?:\\/[^\\s<>"\')\\]]*)?',
  'g',
);
const TRAILING = /[.,;:!?)\]}'"]+$/;
// Si el carácter previo es uno de estos, el "dominio" es parte de un token mayor → descartar.
const BOUNDARY_BEFORE = /[A-Za-z0-9@./\\-]/;
const MAX_URLS = 8;

interface Candidate { raw: string; strict: boolean; }

export function extractUrls(content: string, currentOrigin?: string): string[] {
  if (!content)
    return [];

  const text = content.replace(EMAIL_RE, ' '); // emails fuera
  const candidates: Candidate[] = [];

  // 1) URLs explícitas (http/www) — laxas
  for (const m of text.match(EXPLICIT_RE) ?? [])
    candidates.push({ raw: m, strict: false });

  // 2) dominios pelados — sobre el texto SIN las explícitas (no duplicar su host)
  const rest = text.replace(EXPLICIT_RE, ' ');
  for (const m of rest.matchAll(BARE_RE)) {
    const idx = m.index ?? 0;
    const before = idx > 0 ? rest[idx - 1] : '';
    if (before && BOUNDARY_BEFORE.test(before))
      continue; // parte de un token mayor (ruta, versión, etc.)

    const labels = m[0].split('/')[0].split('.');
    const sld = labels[labels.length - 2] ?? '';
    if (!/[a-z]/.test(sld))
      continue; // el label registrable debe tener ≥1 letra (mata 3.14→3.com)

    candidates.push({ raw: m[0], strict: true });
  }

  // 3) normalizar + validar + filtrar
  const seen = new Set<string>();
  const out: string[] = [];
  for (const { raw, strict } of candidates) {
    const cleaned = raw.replace(TRAILING, '');
    const url = /^https?:\/\//i.test(cleaned) ? cleaned : `https://${cleaned}`;

    let parsed: URL;
    try { parsed = new URL(url); } catch { continue; }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      continue;

    const host = parsed.hostname.toLowerCase();
    if (host.length < 4 || !host.includes('.'))
      continue;

    // whitelist de TLD SOLO para dominios pelados (las explícitas pasan tal cual)
    if (strict && !ALLOWED_TLDS.includes(host.split('.').pop() ?? ''))
      continue;

    if (currentOrigin && parsed.origin === currentOrigin)
      continue;

    if (seen.has(parsed.href))
      continue;

    seen.add(parsed.href);
    out.push(url);
    if (out.length >= MAX_URLS)
      break;
  }

  return out;
}
